import React, { useCallback, useEffect } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import IconButton from "@material-ui/core/IconButton";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import { withStyles } from "@material-ui/core/styles";
import MuiAccordion from "@material-ui/core/Accordion";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import Paper from "@material-ui/core/Paper";

import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";

import { useAppContext } from "../../api/AppContext";
import Loader from "../shared/Loader";
import { useModals } from "../../hooks/useModals";
import useLocalStorage from "../../hooks/useLocalStorage";
import Timestamps from "../Timestamps";

const Accordion = withStyles({
  root: {
    "&:before": {
      height: 0,
    },
    backgroundColor: "transparent",
    boxShadow: "none",
    width: "100%",
  },
})(MuiAccordion);

const AccordionSummary = withStyles({
  content: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
  },
})(MuiAccordionSummary);

const A = styled.a`
  text-decoration: none;
`;

const SpecialLinks = () => {
  const {
    fetchSpecialLinks,
    specialLinks,
    isSpecialLinksLoading,
    isJourneysLoading,
    updateSpecialLink,
    deleteSpecialLink,
    fetchJourneys,
    user,
    journeys,
  } = useAppContext();

  const modals = useModals();

  const [expanded, setExpanded] = useLocalStorage(
    `${user.sub}.SpecialLinks.Expanded`,
    {}
  );

  const handleExpandClick = useCallback(
    (step) => () => {
      setExpanded((expanded) => ({
        ...expanded,
        [step]: !expanded[step],
      }));
    },
    [setExpanded]
  );

  useEffect(() => {
    if (!journeys.length) {
      fetchJourneys();
    }
    fetchSpecialLinks({ all: true });
  }, []);

  const handleCreate = useCallback(() => {
    modals.openModal(modals.modalTypes.EditSpecialLink, {
      isNew: true,
      onConfirm: (specialLink) =>
        updateSpecialLink({
          isNew: true,
          ...specialLink,
        }),
    });
  }, [modals, updateSpecialLink]);

  const handleEdit = useCallback(
    (specialLink) => (e) => {
      e.stopPropagation();
      modals.openModal(modals.modalTypes.EditSpecialLink, {
        item: specialLink,
        onConfirm: (specialLink) => updateSpecialLink(specialLink),
      });
    },
    [modals, updateSpecialLink]
  );

  const { enqueueSnackbar } = useSnackbar();

  const handleEditSemantic = useCallback(
    ({ journeyId, id, type }) =>
      () => {
        const journey = journeys.find(({ id }) => id === journeyId);

        if (!journey) {
          enqueueSnackbar("Journey doesn't exist", { variant: "error" });
          return;
        }
        window.open(
          `/journeys/${journeyId}/${
            journey.locale
          }/${type.toLowerCase()}s/${id}`,
          "_blank"
        );
      },
    [enqueueSnackbar, journeys]
  );

  const handleDelete = useCallback(
    ({ id, name, primes, nudges }) =>
      (e) => {
        e.stopPropagation();

        if (primes?.length || nudges?.length) {
          enqueueSnackbar(
            "This special link is used in the tips and nudges. Substitute it and then you can delete this Special link.",
            {
              variant: "error",
            }
          );
          setExpanded((expanded) => ({
            ...expanded,
            [id]: true,
          }));
          return;
        }

        modals.openConfirmation({
          onConfirm: () => deleteSpecialLink({ id }),
          text: `You want to delete special link "${name}"`,
        });
      },
    [deleteSpecialLink, modals]
  );

  if (isSpecialLinksLoading || isJourneysLoading) {
    return <Loader />;
  }

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <Typography variant="h6">Special links</Typography>
            </Grid>
            <Grid item>
              <Typography color="textSecondary" variant="body2">
                Use this section to add and maintain special links that will be
                available across journeys as CTA for tips and nudges.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Button
            color="primary"
            endIcon={<AddIcon />}
            variant="contained"
            onClick={handleCreate}
          >
            Add new special link
          </Button>
        </Grid>
        <Grid item>
          {specialLinks.map((specialLink, i) => (
            <Accordion
              key={i}
              expanded={Boolean(expanded[i])}
              onChange={handleExpandClick(i)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid
                  container
                  justifyContent="space-between"
                  spacing={1}
                  style={{ width: "100%" }}
                >
                  <Grid item style={{ flexGrow: 1 }}>
                    <Grid container direction="column" spacing={0.5}>
                      <Grid item>
                        <Typography variant="h6">{specialLink.name}</Typography>
                      </Grid>
                      <Grid item>
                        <Typography color="textSecondary" variant="body2">
                          Used in {specialLink.primes?.length || 0} tips and{" "}
                          {specialLink.nudges?.length || 0} nudges
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Tooltip title="Edit">
                          <IconButton onClick={handleEdit(specialLink)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      <Grid item>
                        <Tooltip title="Delete">
                          <IconButton onClick={handleDelete(specialLink)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Paper style={{ width: "100%" }} variant="outlined">
                  <Box p={2}>
                    <Grid container direction="column" spacing={2}>
                      <Grid item>
                        <Grid item>
                          <Timestamps
                            createdAt={specialLink.createdAt}
                            createdBy={specialLink.createdBy}
                            updatedAt={specialLink.updatedAt}
                            updatedBy={specialLink.updatedBy}
                          />
                        </Grid>
                        <List subheader={<ListSubheader>Tips</ListSubheader>}>
                          {specialLink.primes?.map(
                            ({ id, title, text, journeyId, journey, type }) => (
                              <ListItem key={id}>
                                <ListItemText
                                  primary={
                                    <>
                                      {journey && (
                                        <>
                                          <A
                                            href={`/journeys/${journey.id}/${journey.locale}`}
                                            rel="noreferrer noopener"
                                            target="_blank"
                                          >
                                            (
                                            {journey.internalName ||
                                              journey.name}
                                            )
                                          </A>{" "}
                                        </>
                                      )}
                                      {title}
                                    </>
                                  }
                                  secondary={text}
                                />
                                <ListItemSecondaryAction>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      onClick={handleEditSemantic({
                                        id,
                                        journeyId,
                                        type,
                                      })}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                </ListItemSecondaryAction>
                              </ListItem>
                            )
                          )}
                        </List>
                      </Grid>
                      <Grid item>
                        <List subheader={<ListSubheader>Nudges</ListSubheader>}>
                          {specialLink.nudges?.map(
                            ({ id, title, text, journeyId, type, journey }) => (
                              <ListItem key={id}>
                                <ListItemText
                                  primary={
                                    <>
                                      {journey && (
                                        <>
                                          <A
                                            href={`/journeys/${journey.id}/${journey.locale}`}
                                            rel="noreferrer noopener"
                                            target="_blank"
                                          >
                                            (
                                            {journey.internalName ||
                                              journey.name}
                                            )
                                          </A>{" "}
                                        </>
                                      )}
                                      {title}
                                    </>
                                  }
                                  secondary={text}
                                />
                                <ListItemSecondaryAction>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      onClick={handleEditSemantic({
                                        id,
                                        journeyId,
                                        type,
                                      })}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                </ListItemSecondaryAction>
                              </ListItem>
                            )
                          )}
                        </List>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SpecialLinks;
